"use client";

import React, { useEffect, useState } from 'react';
import { FaBars, FaTimes, FaChevronRight } from 'react-icons/fa';
import './Navbar.css';

const SITE_URL = 'https://rupayya.com';

const NAV_MAP = {
  hero:          { url: `${SITE_URL}/`,           description: 'Welcome to Rupayya – India\'s Social Credit Infrastructure' },
  features:      { url: `${SITE_URL}/#features`,  description: 'Explore powerful P2P lending features' },
  communityPods: { url: `${SITE_URL}/#communityPods`, description: 'Borrow and lend within trusted community pods' },
  socialCredit:  { url: `${SITE_URL}/#socialCredit`,  description: 'Your social credit score built on real relationships' },
  p2pLending:    { url: `${SITE_URL}/#p2pLending`,    description: 'Secure peer-to-peer lending between connections' },
  services:      { url: `${SITE_URL}/#services`,  description: 'All Rupayya financial services in one place' },
  faq:           { url: `${SITE_URL}/faq`,         description: 'Frequently asked questions about Rupayya' },
  about:         { url: `${SITE_URL}/about-us`,    description: 'Learn about the Rupayya team and mission' },
  contact:       { url: `${SITE_URL}/contact-us`,  description: 'Get in touch with the Rupayya team' },
  tutorials:     { url: `${SITE_URL}/tutorials`,   description: 'Step-by-step tutorials for using Rupayya' },
  careers:       { url: `${SITE_URL}/careers`,     description: 'Join the Rupayya team – open positions' },
  cta:           { url: `${SITE_URL}/#cta`,        description: 'Download the Rupayya app today' },
};

const getNavUrl  = (id) => NAV_MAP[id]?.url         ?? `${SITE_URL}/#${id}`;
const getNavDesc = (id, label) => NAV_MAP[id]?.description ?? label;

/* ── JSON-LD schemas ──────────────────────────────────────────────────────── */
const buildSchemas = (navItems) => ({
  website: {
    '@context': 'https://schema.org',
    '@type':    'WebSite',
    '@id':      `${SITE_URL}/#website`,
    name:        'Rupayya',
    url:         `${SITE_URL}/`,
    description: 'India\'s Social Credit Infrastructure — secure P2P lending between real connections',
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/search?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  },
  organization: {
    '@context': 'https://schema.org',
    '@type':    'Organization',
    '@id':      `${SITE_URL}/#organization`,
    name:        'Rupayya',
    url:         `${SITE_URL}/`,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/Assets/logo.png`, width: 50, height: 50 },
    sameAs: [
      'https://facebook.com/rupayya',
      'https://twitter.com/rupayya',
      'https://instagram.com/rupayya',
      'https://linkedin.com/company/rupayya',
    ],
  },
  navItems: navItems.map(item => ({
    '@context':   'https://schema.org',
    '@type':      'SiteNavigationElement',
    '@id':        `${SITE_URL}/#nav-${item.id}`,
    name:          item.label,
    description:   getNavDesc(item.id, item.label),
    url:           getNavUrl(item.id),
  })),
});

/* ════════════════════════════════════════════════════════
   NAVBAR COMPONENT
════════════════════════════════════════════════════════ */
const Navbar = ({ scrolled, mobileMenuOpen, setMobileMenuOpen, scrollToSection, navItems }) => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isClient, setIsClient]           = useState(false);

  useEffect(() => { setIsClient(true); }, []);

  useEffect(() => {
    if (!isClient) return;
    const onScroll = () => {
      const pos = window.scrollY + 100;
      for (const item of navItems) {
        const el = document.getElementById(item.id);
        if (el && pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(el.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [navItems, isClient]);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    if (!isClient) return;
    scrollToSection(id);
    if (mobileMenuOpen) setMobileMenuOpen(false);
    window.history.pushState(null, '', `#${id}`);
  };

  const schemas = buildSchemas(navItems);

  return (
    <>
      {/* ── JSON-LD structured data (before <nav> for top-of-document placement) ── */}
      <script type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas.website) }} />
      <script type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas.organization) }} />
      {schemas.navItems.map((schema, i) => (
        <script key={navItems[i].id} type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      ))}

      <nav
        className={`navbar ${scrolled ? 'scrolled' : ''}`}
        role="navigation"
        aria-label="Main navigation"
        itemScope
        itemType="https://schema.org/SiteNavigationElement"
      >
        <div className="nav-container">

          {/* ── Logo ──────────────────────────────────────────────── */}
          <div className="logo">
            <a
              href={`${SITE_URL}/`}
              onClick={(e) => handleNavClick(e, 'hero')}
              className="logo-link"
              aria-label="Rupayya – Go to homepage"
              title="Rupayya Home"
              itemProp="url"
            >
              <div className="logo-icon">
                <picture>
                  <source srcSet="/Assets/logo.png" type="image/webp" />
                  <img
                    src="/Assets/logo.png"
                    alt="Rupayya – India's Social Credit Infrastructure"
                    width="38" height="38"
                    loading="eager"
                    fetchPriority="high"
                    className="logo-img"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      if (e.target.parentElement) {
                        const span = document.createElement('span');
                        span.className = 'logo-fallback';
                        span.textContent = '₹';
                        e.target.parentElement.appendChild(span);
                      }
                    }}
                  />
                </picture>
              </div>
              <span className="logo-text" itemProp="name">Rupayya</span>
            </a>
          </div>

          {/* ── Desktop nav ───────────────────────────────────────── */}
          <nav className="nav-links" aria-label="Page sections">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={getNavUrl(item.id)}
                className={`nav-link ${isClient && activeSection === item.id ? 'active' : ''}`}
                onClick={(e) => handleNavClick(e, item.id)}
                title={getNavDesc(item.id, item.label)}
                aria-current={isClient && activeSection === item.id ? 'page' : undefined}
                itemProp="url"
              >
                <span itemProp="name">{item.label}</span>
              </a>
            ))}
          </nav>

          {/* ── Desktop CTA ───────────────────────────────────────── */}
          <div className="nav-actions">
            <a
              href={`${SITE_URL}/#cta`}
              className="download-btn desktop-only"
              onClick={(e) => handleNavClick(e, 'cta')}
              aria-label="Download Rupayya App"
              title="Download the Rupayya mobile application"
            >
              Download App
            </a>
          </div>

          {/* ── Mobile toggle ─────────────────────────────────────── */}
          {isClient && (
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation-menu"
            >
              {mobileMenuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
            </button>
          )}
        </div>

        {/* ── Mobile drawer ─────────────────────────────────────────── */}
        <div
          id="mobile-navigation-menu"
          className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
          role="navigation"
          aria-label="Mobile navigation menu"
        >
          {navItems.map((item) => (
            <a
              key={item.id}
              href={getNavUrl(item.id)}
              className="mobile-nav-link"
              onClick={(e) => handleNavClick(e, item.id)}
              title={getNavDesc(item.id, item.label)}
              aria-current={isClient && activeSection === item.id ? 'page' : undefined}
            >
              <FaChevronRight aria-hidden="true" className="mobile-nav-icon" />
              <span>{item.label}</span>
            </a>
          ))}
          <a
            href={`${SITE_URL}/#cta`}
            className="mobile-download-btn"
            onClick={(e) => handleNavClick(e, 'cta')}
            aria-label="Download Rupayya App"
          >
            Download App
          </a>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
